import type { AgentUnionCode } from '@/types/ai/agent';
import type { Agent, ProductType } from '@/types/ai/base';
import type { EventData } from '@/types/ai/event';
import type { MESSAGE_TASK_PLAN } from '@/types/ai/message';
import type { MentionItem } from '@/components/ai/text-editor/types';
import type { LLMModel } from '@eureka/shared/select-llm/src/types';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import { AuthModel } from '@eureka/shared/share-tools/src/domain/models/auth.model';
import {
    PsHttpService,
    type PatSnapCommonResponseData,
    type PatSnapSuccessResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface GenerateJobRequest {
    // 要么有input要么有agent_ids、team_ids
    input?: string;
    agent_ids?: number[];
    team_ids?: number[];
    files?: string[];
    // xxx 关闭敏感词检查，只有特殊的agent才能传！
    sensitive_ignore?: boolean;
    agents?: {
        code: string;
        id: number;
        name: string;
    }[];
    agent_codes?: AgentUnionCode[];
    work_project?: string;
    code?: AgentUnionCode;
}

export interface GenerateJobResponse {
    job_id: string;
}

export interface ExecutePlanRequest {
    job_id: string;
    plan: MESSAGE_TASK_PLAN['tasks'];
}

export interface ExecutePlanResponse {
    job_id: string;
}

export interface ContinueJobRequest {
    job_id: string;
    input: string;
    files?: string[];
    model?: LLMModel;
    summary_check?: boolean;
    last_event_id?: string | null;
    // xxx 关闭敏感词检查，只有特殊的agent才能传！
    sensitive_ignore?: boolean;
    work_project?: string;
    agent_code?: AgentUnionCode;
    rag_source?: string[]; // RD业务数据源选择，如 ["patent", "paper", "website"]
}

export interface GetInitResponse {
    agent_model?: 'planning' | 'seek_agent',
    custom_table_permission: boolean;
    is_create_agent: boolean;
    tab_labels: Array<{
        is_freemium: boolean;
        product_type: ProductType;
    }>;
}

export interface JobStreamRequest {
    job_id: string;
    reply?: boolean;
    last_event_id?: string | null;
}

export interface JobStreamLoopRequest {
    job_id: string;
    reply?: boolean;
    last_event_id?: string | null;
}

export interface JobStreamLoopResponse {
    events: EventData[];
    last_event_id: string | null;
}

export interface TeamAgentResponse {
    id: string;
}

export interface JobRunRequest {
    job_id: string;
    last_event_id: string;
    work_project?: string;
}

export interface JobInputResponse {
    company_id: string;
    config: GenerateJobRequest;
    created_at: string;
    error?: string;
    job_id: string;
    progress: number;
    result?: string;
    status: string;
    updated_at: string;
    user_id: string;
}

export interface UploadFilesRequest extends Record<string, any> {
    file: File | Blob;
}

export interface UploadFilesResponse {
    file_url: string;
}

export interface SignFilesRequest {
    file_keys: string[];
    expire?: number;
}

export interface SignFilesResponse {
    signed_urls: {
        sub_resource: string;
        url: string;
    }[];
}

export interface GetAgentDetailRequest {
    code: string;
    type: 'agent' | 'team';
}

export interface QuickJobRequest {
    code: string; // agent唯一标识
    input: string;
    model?: LLMModel;
    summary_check?: boolean;
    work_project?: string;
    rag_source?: string[]; // RD业务数据源选择，如 ["patent", "paper", "website"]
}

export interface GetToolIconRequest {
    tool_id: number;
}

export interface GetToolIconResponse {
    tool_id: number;
    tool_name: string;
    tool_alias: string;
    icon_url: string;
}

export interface GetAgentListRequest {
    page?: number;
    size?: number;
    status?: string;
    keyword?: string;
}

export interface GetAgentListResponse {
    page: number;
    size: number;
    items: Agent[];
}

export type GetAgentDetailResponse = Agent;

export interface GetAtSuggestionListRequest {
    keyword: string;
    page: number;
    size: number;
}

export interface GetAtSuggestionListResponse {
    agents: {
        total: number;
        page?: number;
        size?: number;
        items: MentionItem<Agent>[];
    };
    projects: {
        total: number;
        page?: number;
        size?: number;
        items: MentionItem<any>[]; // 目前还没接入，先占位
    };
}

export interface AgentApi {
    executePlan(data: ExecutePlanRequest): Promise<ExecutePlanResponse>;
    generateJob(data: GenerateJobRequest): Promise<GenerateJobResponse>;
    continueJob(data: ContinueJobRequest): Promise<boolean>;
    getAgentList(params: GetAgentListRequest): Promise<GetAgentListResponse>;
    getAgentDetail(data: GetAgentDetailRequest): Promise<GetAgentDetailResponse>;
    getJobStream(data: JobStreamRequest): PsHttpSSE<EventData>;
    getJobStreamLoop(data: JobStreamRequest): Promise<JobStreamLoopResponse>;
    createTeamAgent(input: string): Promise<TeamAgentResponse>;
    chatJob(data: GenerateJobRequest): Promise<GenerateJobResponse>;
    quickChatJob(data: QuickJobRequest): Promise<GenerateJobResponse>;
    quickChatJobForHomeGuest(data: QuickJobRequest): Promise<GenerateJobResponse>;
    runJob(data: JobRunRequest): Promise<JobRunRequest>;
    getJobInput(job_id: string): Promise<JobInputResponse>;
    terminateJob(job_id: string): Promise<GenerateJobResponse>;
    signFiles(data: SignFilesRequest): Promise<SignFilesResponse>;
    getToolIcon(data: GetToolIconRequest): Promise<GetToolIconResponse>;
    uploadFile(
        file: UploadFilesRequest,
        onProgress?: (e: any) => void
    ): Promise<UploadFilesResponse>;
    uploadFileForSpecialAgent(
        file: UploadFilesRequest,
        onProgress?: (e: any) => void
    ): Promise<UploadFilesResponse>;
    getMonitorJobs(job_id: string): Promise<any>;
    getAtSuggestionList(data: GetAtSuggestionListRequest): Promise<GetAtSuggestionListResponse>;
}

export const AgentApi = Symbol('AgentApi');

@injectable()
export class AgentApiImpl extends PsHttpService implements AgentApi {
    get options() {
        return {
            // baseURL: 'http://192.168.17.107:8001',
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/ai-agent-api`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    getToolIcon(data: GetToolIconRequest) {
        return this.get<PatSnapCommonResponseData<GetToolIconResponse>>({
            url: `/tools/${data.tool_id}/icon`,
            method: 'GET',
        }).then((res) => {
            return res.data as unknown as GetToolIconResponse;
        });
    }

    executePlan(data: ExecutePlanRequest) {
        return this.post<PatSnapCommonResponseData<ExecutePlanResponse>>({
            url: '/job/execute-plan',
            data: {
                ...data,
                content: '',
            },
        }).then((res) => {
            return res as unknown as ExecutePlanResponse;
        });
    }

    generateJob(data: GenerateJobRequest) {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: '/job/generate',
            data,
        }).then((res) => {
            return res as unknown as GenerateJobResponse;
        });
    }

    continueJob(data: ContinueJobRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: `/job/${data.job_id}/reply`,
            headers:
                data.sensitive_ignore === true ? { 'x-sensitive-ignore': 'true' } : {},
            data,
        }).then((res) => {
            return res as unknown as boolean;
        });
    }

    getAgentDetail(data: GetAgentDetailRequest) {
        const types: Record<GetAgentDetailRequest['type'], number> = {
            agent: 0,
            team: 1,
        };
        return this.post<PatSnapCommonResponseData<GetAgentDetailResponse>>({
            url: '/scenario/detail',
            data: {
                code: data.code,
                type: types[data.type],
            },
        }).then((res) => {
            return res as unknown as GetAgentDetailResponse;
        });
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    getJobStream(data: JobStreamRequest) {
        return this._factorySsePsHttp<EventData>()
            .init({
                url: `${this.options.baseURL}/job/messages`,
                data: {
                    job_id: data.job_id,
                    reply: data.reply,
                    last_event_id: data.last_event_id,
                },
                hasFinalMessage: () => true,
            })
            .setFetchHeaders({
                'X-API-Version': '1.0',
            });
    }

    getJobStreamLoop(data: JobStreamLoopRequest) {
        return this.post<PatSnapSuccessResponseData<JobStreamLoopResponse>>({
            url: '/job/poll-messages',
            data,
        }).then((res) => {
            return res.data;
        });
    }

    getAgentList(params: GetAgentListRequest) {
        return this.get<PatSnapSuccessResponseData<GetAgentListResponse>>({
            url: '/agents',
            params,
        }).then((res) => {
            return res.data
        });
    }

    createTeamAgent(input: string) {
        return this.post<PatSnapSuccessResponseData<TeamAgentResponse>>({
            url: '/scenario/create',
            data: {
                input,
            },
        }).then((res) => {
            return res.data;
        });
    }

    chatJob(data: GenerateJobRequest) {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: '/job/chat',
            headers:
                data.sensitive_ignore === true ? { 'x-sensitive-ignore': 'true' } : {},
            data,
        }).then((res) => {
            return res as unknown as GenerateJobResponse;
        });
    }

    quickChatJob(data: QuickJobRequest) {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: '/job/quick-chat',
            data,
        }).then((res) => {
            return res as unknown as GenerateJobResponse;
        });
    }

    quickChatJobForHomeGuest(data: QuickJobRequest) {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/guest/ai-agent-api/job/quick-chat`,
            data,
        }).then((res) => {
            return res as unknown as GenerateJobResponse;
        });
    }

    runJob(data: JobRunRequest) {
        return this.post<PatSnapCommonResponseData<JobRunRequest>>({
            url: '/job/run',
            data,
        }).then((res) => {
            return res as unknown as JobRunRequest;
        });
    }

    @inject(AuthModel)
    private _authModel!: AuthModel;

    getJobInput(job_id: string) {
        const { isShare, isLogin } = this._authModel.modelData;
        return this.get<PatSnapCommonResponseData<GenerateJobRequest>>({
            url: isShare || !isLogin ? `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/share/ai-agent-api/job/${job_id}` : `/job/${job_id}`,
        }).then((res) => {
            return res as unknown as JobInputResponse;
        });
    }

    terminateJob(job_id: string): Promise<GenerateJobResponse> {
        return this.post<PatSnapCommonResponseData<GenerateJobResponse>>({
            url: `/job/${job_id}/terminate`,
        }).then((res) => {
            return res as unknown as GenerateJobResponse;
        });
    }

    // 暂且支持一个文件
    uploadFile(options: UploadFilesRequest, onProgress?: (e: any) => void) {
        const formData = new FormData();
        for (const key in options) {
            formData.append(key, (options as any)[key]);
        }
        return this.post<PatSnapCommonResponseData<UploadFilesResponse>>({
            url: '/toolkits/upload-file',
            data: formData,
            onUploadProgress: onProgress,
        }).then((res) => {
            return res as unknown as UploadFilesResponse;
        });
    }

    // 特定 agent 使用的上传接口（eurekals 服务）
    uploadFileForSpecialAgent(options: UploadFilesRequest, onProgress?: (e: any) => void) {
        const formData = new FormData();
        for (const key in options) {
            formData.append(key, (options as any)[key]);
        }
        // 使用 eurekals 服务的上传接口
        const eurekaLsBaseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0`;
        return this.post<PatSnapCommonResponseData<UploadFilesResponse>>({
            url: `${eurekaLsBaseURL}/agent/file/upload`,
            data: formData,
            onUploadProgress: onProgress,
        }).then((res) => {
            return res as unknown as UploadFilesResponse;
        });
    }

    signFiles(data: SignFilesRequest) {
        return this.post<PatSnapCommonResponseData<SignFilesResponse>>({
            url: '/toolkits/sign-files',
            data,
            cache: true,
        }).then((res) => {
            return res as unknown as SignFilesResponse;
        });
    }

    getMonitorJobs(job_id: string) {
        return this.get<PatSnapCommonResponseData<any>>({
            url: `/monitor/jobs/${job_id}`,
        }).then((res) => {
            return res as unknown as any;
        });
    }

    getAtSuggestionList(params: GetAtSuggestionListRequest) {
        return this.get<PatSnapSuccessResponseData<GetAtSuggestionListResponse>>({
            url: '/agents/links',
            params,
        }).then((res) => {
            return res.data;
        });
    }
}
